<!-- app-subdomain-creators/src/Components/CreatorChatMemberListComponent.vue -->

<template>
  <div class="w-full h-full border border-zinc-200 rounded-lg flex flex-col bg-white">

    <!-- TOP FILTER BAR -->
    <div class="p-3 border-b border-zinc-100">

      <!-- Product Channels Dropdown -->
      <div
        class="relative mb-3"
        @mouseenter="handleChannelsMouseEnter"
        @mouseleave="handleChannelsMouseLeave"
      >
        <button
          @click.stop="toggleChannelsDropdown"
          class="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold border border-zinc-200 rounded-md hover:bg-zinc-50"
        >
          {{ isLoadingChannels
              ? 'Loading...'
              : (selectedChannel ? selectedChannel.name : 'Select Chat') }}
          <span class="text-zinc-400">▾</span>
        </button>

        <!-- Dropdown -->
        <div
          v-if="channelsOpen"
          class="absolute mt-1 w-full bg-white border border-zinc-200 rounded-md shadow-lg z-10"
        >
          <div
            v-for="channel in productChannels"
            :key="channel.id"
            @click="selectChannel(channel)"
            class="px-3 py-2 text-sm hover:bg-zinc-50 cursor-pointer"
          >
            {{ channel.name }}
          </div>

          <div
            class="border-t border-zinc-100 px-3 py-2 text-sm text-[#2D8CFF] cursor-pointer hover:bg-zinc-50"
          >
            + Add Product Channel
          </div>
        </div>
      </div>

      <!-- OPEN / CLOSED / SAVED / ALL SWITCH -->
      <div class="flex bg-zinc-100 rounded-md p-1 mb-3">
        <button
          v-for="tab in tabs"
          :key="tab"
          @click="activeTab = tab"
          class="flex-1 text-xs py-1.5 rounded-md font-semibold transition"
          :class="activeTab === tab
            ? 'bg-white shadow text-zinc-900'
            : 'text-zinc-500 hover:text-zinc-700'"
        >
          {{ tab }}
        </button>
      </div>

      <!-- SEARCH -->
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search Members"
        class="w-full px-3 py-2 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D8CFF]"
      />
    </div>

    <!-- MEMBER LIST -->
    <div ref="membersScrollEl" class="chat-scroll flex-1 overflow-y-auto scroll-smooth scroll-p-8 mt-1 mr-1">

      <!-- Loading state -->
      <div
        v-if="isLoadingMembers"
        class="text-center text-sm text-zinc-400 py-10"
      >
        Loading members...
      </div>

      <!-- Members -->
      <template v-else>
        <div
          v-for="member in filteredMembers"
          :key="member.id"
          @click="handleSelectMember(member)"
          class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-50"
          :class="member.active ? 'bg-zinc-50' : ''"
        >
          
        <!-- Avatar + presence dot -->
        <div class="relative">
            <img
              :src="member.avatar || defaultAvatar"
              alt="avatar"
              class="w-9 h-9 rounded-full object-cover"
              @error="($event.target.src !== defaultAvatar) && ($event.target.src = defaultAvatar)"
            />
            <div
              class="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white"
              :class="{
                'bg-green-500': memberStatus(member) === 'online',
                'bg-yellow-400': memberStatus(member) === 'away',
                'bg-gray-300': memberStatus(member) !== 'online' && memberStatus(member) !== 'away'
              }"
            ></div>
          </div>

          <!-- Text -->
          <div class="flex-1 min-w-0">
            <div class="flex justify-between items-center">
              <p class="text-sm font-semibold truncate">
                {{ member.name }}
              </p>

              <!-- Time -->
              <span
                v-if="member.unreadCount === 0"
                class="text-[11px] text-zinc-400"
              >
                {{ member.time }}
              </span>
            </div>

            <!-- Last message OR typing -->
            <div
              class="text-xs flex items-center gap-1 min-w-0"
              :class="member.isTyping ? 'text-[#2D8CFF] font-medium' : 'text-zinc-500'"
            >
              <template v-if="member.isTyping">
                <!-- the ONLY thing that should truncate -->
                <span class="flex-1 min-w-0 truncate text-[10px]">
                  {{ member.name }} is typing
                </span>

                <!-- dots + icon should never force width -->
                <span class="typing-dots text-lg leading-none shrink-0">
                  <span>.</span><span>.</span><span>.</span>
                </span>
                <PencilIcon class="w-3.5 h-3.5 text-[#2D8CFF] shrink-0" />
              </template>

              <template v-else>
                <span class="flex-1 min-w-0 truncate">
                  {{ member.lastMessage }}
                </span>
              </template>
            </div>
          </div>

          <!-- UNREAD BADGE -->
          <div
            v-if="member.unreadCount > 0"
            class="bg-[#2D8CFF] text-white text-[12px] font-semibold
                   min-w-[20px] h-5 px-1.5
                   rounded-full flex items-center justify-center"
          >
            {{ member.unreadCount > 20 ? '20+' : member.unreadCount }}
          </div>
        </div>

          <!-- Empty State -->
        <div
          v-if="filteredMembers.length === 0 && !isLoadingMembers"
          class="text-center text-sm text-zinc-400 py-10"
        >
          No conversations found
        </div>

        <!-- Infinite scroll sentinel (used by IntersectionObserver) -->
        <div
          v-if="filteredMembers.length > 0"
          ref="membersSentinel"
          class="h-8"
        ></div>

        <!-- Bottom loading indicator when fetching next page -->
        <div
          v-if="isLoadingMoreMembers"
          class="text-center text-xs text-zinc-400 py-2"
        >
          Loading more...
        </div>

      </template>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { PencilIcon } from '@heroicons/vue/24/outline'
import { useWebsocketStore } from '@/stores/websocketStore'
import { usePresenceStore } from '@/stores/presenceStore'

import defaultAvatar from '@/assets/media/images/default-avatar.png'

const emit = defineEmits(['select-member', 'select-channel'])

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL
const websocketStore = useWebsocketStore()
const presenceStore = usePresenceStore()

const TYPING_TIMEOUT_MS = 18000
const SEARCH_DEBOUNCE_MS = 250
const typingTimeouts = new Map()
let unsubscribeChatTyping = null
let unsubscribeChatNewMessage = null
let searchDebounceTimer = null

// 🔥 prevents out-of-order fetch overwrites when switching channels quickly
let conversationsFetchToken = 0

// 🔁 Pagination / infinite scroll
const PAGE_SIZE = 3
const conversationsOffset = ref(0)   // OFFSET on backend
const hasMoreMembers = ref(true)
const isLoadingMoreMembers = ref(false)

// Scroll container + sentinel for IntersectionObserver
const membersScrollEl = ref(null)
const membersSentinel = ref(null)
let membersObserver = null

/* ------------------------------------------
   Product Channels (from backend)
------------------------------------------ */
const productChannels = ref([])
const selectedChannel = ref(null)
const channelsOpen = ref(false)

const isLoadingChannels = ref(false)
const channelError = ref(null)

const channelsHideTimer = ref(null)

const toggleChannelsDropdown = () => {
  if (channelsHideTimer.value) {
    clearTimeout(channelsHideTimer.value)
    channelsHideTimer.value = null
  }

  channelsOpen.value = !channelsOpen.value
}

const scheduleHideChannelsDropdown = () => {
  if (channelsHideTimer.value) {
    clearTimeout(channelsHideTimer.value)
  }

  channelsHideTimer.value = setTimeout(() => {
    channelsOpen.value = false
    channelsHideTimer.value = null
  }, 150)
}

const handleChannelsMouseEnter = () => {
  if (channelsHideTimer.value) {
    clearTimeout(channelsHideTimer.value)
    channelsHideTimer.value = null
  }
}

const handleChannelsMouseLeave = () => {
  if (channelsOpen.value) {
    scheduleHideChannelsDropdown()
  }
}

async function fetchFeatureInstances() {
  isLoadingChannels.value = true
  channelError.value = null

  try {
    const response = await fetch(`${apiProxyUrl}/api/feature-instances`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch feature instances: ${response.status}`)
    }

    const data = await response.json()
    const instances = data.instances || []

    // 🔹 Filter to ONLY Chat feature instances
    const chatInstances = instances.filter(inst => {
      const name = (inst.feature_name || '').toLowerCase()
      const slug = (inst.slug || '').toLowerCase()
      if (name.includes('chat')) return true
      if (slug.includes('chat')) return true
      return false
    })

    const mappedChannels = chatInstances.map(inst => ({
      id: inst.instance_id,
      name: inst.custom_name || inst.slug || inst.feature_name || `Feature #${inst.instance_id}`,
      raw: inst,
    }))

    productChannels.value = mappedChannels
    selectedChannel.value = mappedChannels[0] || null

    if (selectedChannel.value) {
      emit('select-channel', selectedChannel.value)

      // reset pagination for initial load
      conversationsOffset.value = 0
      hasMoreMembers.value = true

      await fetchConversationsForSelectedChannel({ append: false })
    }
  } catch (err) {
    console.error('Error fetching feature instances:', err)
    channelError.value = err.message || 'Failed to load feature instances'
  } finally {
    isLoadingChannels.value = false
  }
}

async function selectChannel(channel) {
  selectedChannel.value = channel
  channelsOpen.value = false
  emit('select-channel', channel)
  // fetching is handled by watch(selectedChannel)
}

/* ------------------------------------------
   Helpers
------------------------------------------ */
const activeMemberId = ref(null)

const formatTimeShort = (value) => {
  if (!value) return ''
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const normalizeStatus = (v) => {
  const s = String(v || '').toLowerCase()
  if (s === 'closed') return 'Closed'
  if (s === 'saved') return 'Saved'
  if (s === 'open') return 'Open'
  return 'All'
}

const getMemberPresenceId = (member) => {
  if (!member) return null
  return (
    member.user_id ??
    member.userId ??
    member.id ??
    null
  )
}

const memberStatus = (member) => {
  const id = getMemberPresenceId(member)
  if (!id) return 'offline'
  return presenceStore.memberStatus(id)
}

const resortMembersList = () => {
  // Create a new sorted array to keep Vue reactivity happy
  members.value = [...members.value].sort((a, b) => {
    const aUnread = Number(a.unreadCount || 0) > 0 ? 1 : 0
    const bUnread = Number(b.unreadCount || 0) > 0 ? 1 : 0

    // 1) Unread conversations on top (true > false)
    if (aUnread !== bUnread) {
      return bUnread - aUnread
    }

    // 2) Then sort by lastTs (newest first)
    const aTs = Number(a.lastTs || 0)
    const bTs = Number(b.lastTs || 0)
    return bTs - aTs
  })
}


/* ------------------------------------------
   Tabs + Search
------------------------------------------ */
const tabs = ['Open', 'Closed', 'Saved', 'All']
const activeTab = ref('Open')
const searchQuery = ref('')

/* ------------------------------------------
   Members (REAL data from backend)
------------------------------------------ */
const members = ref([])
const isLoadingMembers = ref(false)
const membersError = ref(null)

async function fetchConversationsForSelectedChannel(options = {}) {
  const { append = false } = options

  if (!append) {
    isLoadingMembers.value = true
    // when starting fresh (first page), reset offset
    conversationsOffset.value = 0
    hasMoreMembers.value = true
  }
  membersError.value = null

  const myToken = ++conversationsFetchToken

  try {
    if (!selectedChannel.value?.id) {
      if (!append) {
        members.value = []
        hasMoreMembers.value = false
        conversationsOffset.value = 0
      }
      return
    }

    const requestedFeatureId = Number(selectedChannel.value.id)

    const url = new URL(`${apiProxyUrl}/api/chat-conversations`)
    url.searchParams.append('featureInstanceId', String(requestedFeatureId))
    url.searchParams.append('limit', String(PAGE_SIZE))
    url.searchParams.append('offset', String(conversationsOffset.value))
    url.searchParams.append('status', String(activeTab.value || 'All'))

    const trimmedSearch = (searchQuery.value || '').trim()
    if (trimmedSearch) {
      url.searchParams.append('q', trimmedSearch)
    }

    console.log('[CreatorChat] fetchConversationsForSelectedChannel', {
  append,
  featureInstanceId: requestedFeatureId,
  limit: PAGE_SIZE,
  offset: conversationsOffset.value,
  status: activeTab.value,
  q: trimmedSearch,
})

    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })

    // ignore stale response
    if (myToken !== conversationsFetchToken || Number(selectedChannel.value?.id) !== requestedFeatureId) return

    if (response.status === 404) {
      if (!append) {
        members.value = []
        hasMoreMembers.value = false
      }
      return
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch chat conversations: ${response.status}`)
    }

    const data = await response.json()

    // ignore stale JSON parse result
    if (myToken !== conversationsFetchToken || Number(selectedChannel.value?.id) !== requestedFeatureId) return

    const rows =
      data.conversations ||
      data.rows ||
      data.items ||
      data.results ||
      []

    const existingById = new Map((members.value || []).map((m) => [String(m.id), m]))

    const nextList = rows.map((row) => {
      const memberIdRaw =
        row.member_id ??
        row.memberId ??
        row.user_id ??
        row.userId ??
        row.id

      const memberId = Number(memberIdRaw)

      const fullName = [
        row.first_name || row.member_first_name,
        row.last_name || row.member_last_name
      ].filter(Boolean).join(' ').trim()

      const name =
        fullName ||
        row.name ||
        row.member_name ||
        row.email ||
        row.member_email ||
        'Unknown Member'

      const alias = row.alias ?? null
      const handle = row.handle ?? row.member_handle ?? null

      const memberStatusValue =
        row.memberStatus ??
        row.member_status ??
        null

      const avatarUrl =
        (row.profile_pic || row.avatar || row.member_avatar || '').toString().trim().length > 0
          ? (row.profile_pic || row.avatar || row.member_avatar)
          : defaultAvatar

      const lastMessage =
        row.last_message ||
        row.lastMessage ||
        row.last_message_body ||
        row.lastMessageBody ||
        row.body ||
        row.lastMessage ||
        ''

      const lastAt =
        row.last_message_at ||
        row.lastMessageAt ||
        row.last_message_created_at ||
        row.lastMessageCreatedAt ||
        row.updated_at ||
        row.updatedAt ||
        null

        const lastTs = lastAt ? new Date(lastAt).getTime() : 0

      const unreadCountRaw =
        row.unread_count ??
        row.unreadCount ??
        row.unread ??
        0

      const statusRaw =
        row.status ||
        row.conversation_status ||
        row.conversationStatus ||
        'Open'

      const reused = existingById.get(String(memberId))

      if (reused) {
        reused.name = name
        reused.profileName = name
        reused.alias = alias
        reused.handle = handle || reused.handle || null

        reused.memberStatus = memberStatusValue ?? reused.memberStatus ?? null

        reused.username = row.email || row.member_email || reused.username || ''
        reused.email = row.email || row.member_email || reused.email || ''
        reused.plan = row.subscription_plan_name || reused.plan || ''
        reused.avatar = avatarUrl

        reused.lastMessage = String(lastMessage || '').trim()
        reused.time = formatTimeShort(lastAt)
        reused.lastTs = lastTs
        reused.status = normalizeStatus(statusRaw)

        const apiUnread = Number(unreadCountRaw) || 0
        const localUnread = Number(reused.unreadCount) || 0
        reused.unreadCount = Math.max(apiUnread, localUnread)

        reused.active = Number(reused.id) === Number(activeMemberId.value)
        return reused
      }

      return {
        id: memberId,

        name,
        profileName: name,
        alias,
        handle,
        memberStatus: memberStatusValue,

        username: row.email || row.member_email || '',
        email: row.email || row.member_email || '',
        plan: row.subscription_plan_name || '',
        avatar: avatarUrl,
        tags: [],

        lastMessage: String(lastMessage || '').trim(),
        time: formatTimeShort(lastAt),
        lastTs,
        status: normalizeStatus(statusRaw),
        unreadCount: Number(unreadCountRaw) || 0,

        isTyping: false,
        active: Number(memberId) === Number(activeMemberId.value),
      }
    })

    // final guard
    if (myToken !== conversationsFetchToken || Number(selectedChannel.value?.id) !== requestedFeatureId) return

    const hasMoreFromApi =
      data.hasMore ??
      data.has_more ??
      (rows.length >= PAGE_SIZE)

    hasMoreMembers.value = !!hasMoreFromApi

    if (!append) {
      members.value = nextList
    } else {
      const deduped = [...members.value]
      const seen = new Set(deduped.map((m) => String(m.id)))

      for (const m of nextList) {
        const key = String(m.id)
        if (!seen.has(key)) {
          seen.add(key)
          deduped.push(m)
        }
      }

      members.value = deduped
    }

    const receivedCount = nextList.length
    conversationsOffset.value += receivedCount

    // 🔥 Subscribe to presence updates for all members in this channel
    const memberIdsForPresence = members.value
      .map((m) => getMemberPresenceId(m))
      .filter(Boolean)

    if (memberIdsForPresence.length) {
      websocketStore.send({
        type: 'creator',
        event: 'presence:subscribeMembers',
        payload: {
          memberIds: memberIdsForPresence,
        },
      })
    }
  } catch (err) {
    if (myToken === conversationsFetchToken) {
      console.error('Error fetching chat conversations:', err)
      membersError.value = err.message || 'Failed to load conversations'
      if (!append) {
        members.value = []
      }
      hasMoreMembers.value = false
    }
  } finally {
    if (myToken === conversationsFetchToken && !append) {
      isLoadingMembers.value = false
    }
  }
}

async function loadMoreMembers() {
  if (isLoadingMembers.value || isLoadingMoreMembers.value) return
  if (!hasMoreMembers.value) return
  if (!selectedChannel.value?.id) return

  isLoadingMoreMembers.value = true
  try {
    await fetchConversationsForSelectedChannel({ append: true })
  } catch (err) {
    console.error('[CreatorChatMemberList] loadMoreMembers error:', err)
  } finally {
    isLoadingMoreMembers.value = false
  }
}

const setupMembersObserver = () => {
  if (membersObserver) {
    membersObserver.disconnect()
    membersObserver = null
  }

  if (!membersScrollEl.value || !membersSentinel.value) return

  membersObserver = new IntersectionObserver(
  (entries) => {
    const entry = entries[0]
    if (!entry || !entry.isIntersecting) return

    // Only load more if we are actually near the bottom and there is more
    const root = membersScrollEl.value
    if (!root) return

    const distanceFromBottom = root.scrollHeight - (root.scrollTop + root.clientHeight)

    // e.g. only trigger if we're within 150px of true bottom
    if (distanceFromBottom < 150) {
      loadMoreMembers()
    }
  },
  {
    root: membersScrollEl.value,
    rootMargin: '0px 0px 0px 0px',
    threshold: 0.1,
  }
)


  membersObserver.observe(membersSentinel.value)
}

async function markConversationRead(memberId) {
  if (!selectedChannel.value?.id || !memberId) return

  try {
    const resp = await fetch(`${apiProxyUrl}/api/chat-mark-read`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        featureInstanceId: selectedChannel.value.id,
        memberId,
      }),
    })

    if (!resp.ok) {
      console.warn('[CreatorChatMemberList] mark-read failed:', resp.status)
      return
    }

    const m = members.value.find(x => Number(x.id) === Number(memberId))
    if (m) m.unreadCount = 0
  } catch (e) {
    console.warn('[CreatorChatMemberList] mark-read error:', e)
  }
}

async function handleSelectMember(member) {
  if (!member) return

  activeMemberId.value = member.id

  members.value.forEach((m) => {
    m.active = Number(m.id) === Number(member.id)
  })

  emit('select-member', member)

  await markConversationRead(member.id)
}

/* ------------------------------------------
   WS Event Handlers
------------------------------------------ */
function handleTypingEvent(payload) {
  if (!payload) return

  const meta = payload.meta || payload

  const senderType = meta.senderType || meta.sender_type
  if (senderType === 'creator') return

  const rawFeatureId =
    meta.featureInstanceId ??
    meta.feature_instance_id ??
    meta.featureId ??
    meta.feature_id

  if (!rawFeatureId) return
  if (!selectedChannel.value) return
  if (Number(rawFeatureId) !== Number(selectedChannel.value.id)) return

  const rawMemberId = meta.memberId ?? meta.member_id
  if (!rawMemberId) return

  const memberId = Number(rawMemberId)
  const member = members.value.find((m) => Number(m.id) === memberId)
  if (!member) return

  const typingFlag =
    payload.isTyping ??
    meta.isTyping ??
    meta.typing ??
    (meta.status === 'typing') ??
    true

  const existingTimeout = typingTimeouts.get(memberId)

  if (typingFlag === false) {
    member.isTyping = false

    if (existingTimeout) {
      clearTimeout(existingTimeout)
      typingTimeouts.delete(memberId)
    }

    return
  }

  member.isTyping = true

  if (existingTimeout) {
    clearTimeout(existingTimeout)
  }

  const timeoutId = setTimeout(() => {
    member.isTyping = false
    typingTimeouts.delete(memberId)
  }, TYPING_TIMEOUT_MS)

  typingTimeouts.set(memberId, timeoutId)
}

function handleNewMessageEvent(payload) {
  if (!payload) return

  const meta = payload.meta || payload
  const row = payload.message || payload.chatMessage || payload.messageRow || payload

  const rawFeatureId =
    meta.featureInstanceId ??
    meta.feature_instance_id ??
    row.featureInstanceId ??
    row.feature_instance_id

  if (!rawFeatureId) return
  if (!selectedChannel.value) return
  if (Number(rawFeatureId) !== Number(selectedChannel.value.id)) return

  const rawMemberId =
    meta.memberId ??
    meta.member_id ??
    row.memberId ??
    row.member_id

  if (!rawMemberId) return

  const memberId = Number(rawMemberId)
  const member = members.value.find(m => Number(m.id) === memberId)
  if (!member) return

  const bodyText =
    (row && (row.body || row.message || row.text || row.content)) ||
    member.lastMessage ||
    ''

  member.lastMessage = String(bodyText || '').trim()

  const ts =
    row.created_at ||
    row.createdAt ||
    meta.created_at ||
    meta.createdAt ||
    new Date().toISOString()

  member.time = formatTimeShort(ts)
  member.lastTs = ts ? new Date(ts).getTime() : Date.now()

  if (Number(activeMemberId.value) !== Number(memberId)) {
    member.unreadCount = (Number(member.unreadCount) || 0) + 1

    if (member.status !== 'Open') {
      member.status = 'Open'
    }
  } else {
    member.unreadCount = 0
  }

  member.isTyping = false

  const existingTimeout = typingTimeouts.get(memberId)
  if (existingTimeout) {
    clearTimeout(existingTimeout)
    typingTimeouts.delete(memberId)
  }
   // 🔥 Re-sort list so this conversation bubbles to the top
  resortMembersList()
}

/* ------------------------------------------
   Filtering Logic
------------------------------------------ */
const filteredMembers = computed(() => {
  let list = members.value

  if (activeTab.value !== 'All') {
    list = list.filter((m) => m.status === activeTab.value)
  }

  const rawQuery = (searchQuery.value || '').trim()
  if (!rawQuery) {
    return list
  }

  const tokens = rawQuery.toLowerCase().split(/\s+/).filter(Boolean)

  return list.filter((m) => {
    const haystack = [
      m.name,
      m.profileName,
      m.alias,
      m.handle,
      m.username,
      m.email,
      m.lastMessage,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return tokens.every((t) => haystack.includes(t))
  })
})

// Re-attach IntersectionObserver whenever list changes
watch(filteredMembers, () => {
  nextTick(() => {
    setupMembersObserver()
  })
})

/* ------------------------------------------
   Channel switching
------------------------------------------ */
watch(selectedChannel, () => {
  typingTimeouts.forEach(timeoutId => clearTimeout(timeoutId))
  typingTimeouts.clear()

  activeMemberId.value = null
  members.value = []

  conversationsOffset.value = 0
  hasMoreMembers.value = true

  emit('select-member', null)

  // On channel/chat switch, always land on Open by default.
  // activeTab watcher will trigger the fetch when this changes.
  if (activeTab.value !== 'Open') {
    activeTab.value = 'Open'
    return
  }

  fetchConversationsForSelectedChannel({ append: false })
})

watch(activeTab, () => {
  conversationsOffset.value = 0
  hasMoreMembers.value = true
  fetchConversationsForSelectedChannel({ append: false })
})

watch(searchQuery, () => {
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer)
  }

  searchDebounceTimer = setTimeout(() => {
    conversationsOffset.value = 0
    hasMoreMembers.value = true
    fetchConversationsForSelectedChannel({ append: false })
  }, SEARCH_DEBOUNCE_MS)
})

/* ------------------------------------------
   Lifecycle
------------------------------------------ */
onMounted(() => {
  fetchFeatureInstances()

  websocketStore.connect()

  unsubscribeChatTyping = websocketStore.subscribe('chat:typing', (payload) => {
    handleTypingEvent(payload)
  })

  unsubscribeChatNewMessage = websocketStore.subscribe('chat:newMessage', (payload) => {
    handleNewMessageEvent(payload)
  })
})

onUnmounted(() => {
  if (unsubscribeChatTyping) {
    unsubscribeChatTyping()
    unsubscribeChatTyping = null
  }

  if (unsubscribeChatNewMessage) {
    unsubscribeChatNewMessage()
    unsubscribeChatNewMessage = null
  }

  typingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId))
  typingTimeouts.clear()

  if (channelsHideTimer.value) {
    clearTimeout(channelsHideTimer.value)
    channelsHideTimer.value = null
  }

  if (membersObserver) {
    membersObserver.disconnect()
    membersObserver = null
  }

  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer)
    searchDebounceTimer = null
  }
})
</script>


<style scoped>
/* Typing dots animation */
.typing-dots span {
  animation: blink 1.4s infinite both;
  font-size: 1.1em;
  line-height: 1;
}

.typing-dots span:nth-child(2) {
  animation-delay: .2s;
}
.typing-dots span:nth-child(3) {
  animation-delay: .4s;
}
@keyframes blink {
  0% { opacity: .2; }
  20% { opacity: 1; }
  100% { opacity: .2; }
}

/* ✅ Hover-only, nearly invisible scrollbar */
:deep(.chat-scroll) {
  scrollbar-width: thin;                 /* Firefox */
  scrollbar-color: transparent transparent;
}

/* WebKit base */
:deep(.chat-scroll::-webkit-scrollbar) {
  width: 6px;
  height: 6px;
}

/* ❌ Kill classic buttons (no-op for overlay, harmless otherwise) */
:deep(.chat-scroll::-webkit-scrollbar-button) {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
  background-color: transparent;
}

/* Track fully invisible */
:deep(.chat-scroll::-webkit-scrollbar-track) {
  background: transparent;
}

/* Thumb invisible by default */
:deep(.chat-scroll::-webkit-scrollbar-thumb) {
  background-color: transparent;
  border-radius: 999px;
}

/* ✅ On hover: fade scrollbar in */
:deep(.chat-scroll:hover) {
  scrollbar-color: rgba(203, 213, 225, 0.45) transparent; /* Firefox */
}

:deep(.chat-scroll:hover::-webkit-scrollbar-thumb) {
  background-color: rgba(203, 213, 225, 0.45);
}

/* Slightly stronger on thumb hover */
:deep(.chat-scroll:hover::-webkit-scrollbar-thumb:hover) {
  background-color: rgba(203, 213, 225, 0.65);
}
</style>
